import { Box, styled } from '@mui/material';
import DatasetForm from 'components/ComponentsLibrary/Forms/DatasetForm';
import useDatasetMutation from 'components/ComponentsLibrary/hooks/useDatasetMutation';
import GeoDataVerification from 'components/DataImport/Views/GeoDataVerification';
import { useDueDiligenceProcess } from 'components/DueDiligenceProcess/Context/DueDiligenceProcessContext';
import { useGeoDataCollection } from 'components/DueDiligenceProcess/Context/SubSections/GeoDataCollectionContext';
import DueDiligenceProcessSubSectionNavigation from 'components/DueDiligenceProcess/DueDiligenceProcessSubSectionNavigation';
import { Loader } from 'components/Forms';
import { useMessages } from 'components/hooks';
import {
  datasetSchema,
  IDatasetFormValue,
  IGeoUploadFormValue,
} from 'constants/schemas/geoUpload.schema';
import GeoDataItem from 'designSystem/DataDisplay/GeoDataItem/GeoDataItem';
import { Formik, FormikProps } from 'formik';
import isEqual from 'lodash/isEqual';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { EGeoDataEntity, EGeoDataStatus } from 'types/geodata.types';

const DatasetContainer = styled('div')(() => ({
  cursor: 'pointer',
}));

const GeoDataCollectionVerification: FC = () => {
  const { setErrorMessage } = useMessages();
  const { geoDataProcess } = useDueDiligenceProcess();
  const { updateDataset } = useDatasetMutation();
  const { selectedGeoDataId, onNextClick, onPreviousClick, setSelectedGeoDataId } =
    useGeoDataCollection();
  const formRef = useRef<FormikProps<IDatasetFormValue>>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const selectedGeoData = useMemo(
    () => geoDataProcess?.geoDatas.find(geoData => geoData.id === selectedGeoDataId),
    [geoDataProcess, selectedGeoDataId]
  );

  const initialValues: IDatasetFormValue = {
    ...datasetSchema.default(),
    ...(selectedGeoData?.dataset
      ? {
          datasetId: selectedGeoData.dataset.id,
          title: selectedGeoData.dataset.title,
          rawMaterialId: selectedGeoData.dataset.rawMaterial.id,
          countryCode: selectedGeoData.dataset.originCountry,
          ownedBy: selectedGeoData.dataset.ownedBy,
        }
      : {}),
  };

  useEffect(() => {
    if (!geoDataProcess) {
      return;
    }
    // find the first geo-data that is has the status 'VERIFICATION_FAILED' if exists and if there is not already one in that state selected
    if (selectedGeoDataId && selectedGeoData) {
      if (selectedGeoData.status === EGeoDataStatus.VERIFICATION_FAILED) {
        return;
      }
      const firstFailedGeoData = geoDataProcess.geoDatas.find(
        geoData => geoData.status === EGeoDataStatus.VERIFICATION_FAILED
      );
      if (firstFailedGeoData) {
        setSelectedGeoDataId(firstFailedGeoData.id);
      }
    } else {
      // Otherwise select the first dataset if none is selected
      setSelectedGeoDataId(geoDataProcess.geoDatas[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoDataProcess]);

  const updateDatasetIfNeeded = async (
    values: Partial<
      Pick<IGeoUploadFormValue, 'title' | 'rawMaterialId' | 'countryCode' | 'ownedBy'>
    > & {
      datasetId: string; // datasetid is required
      files?: { id: string }[];
    }
  ) => {
    if (values.datasetId && !isEqual(initialValues, values)) {
      return await updateDataset({
        ...values,
      });
    }
  };

  if (!geoDataProcess) {
    return <Loader />;
  }

  const handleSubmitVerification = async (values: IDatasetFormValue) => {
    if (selectedGeoDataId) {
      try {
        setLoading(true);
        if (selectedGeoData?.datasetId) {
          return await updateDatasetIfNeeded({
            datasetId: selectedGeoData?.datasetId,
            ...values,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error while saving the dataset', error);
        setErrorMessage('Could not save the dataset. Please try again or contact the support');
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * To be able to switch between datasets we need to save the changes of the current dataset
   */
  const handleGeoDataClick = async (geoDataId: string) => {
    if (selectedGeoDataId) {
      try {
        setLoading(true);
        if (selectedGeoData?.datasetId) {
          await updateDatasetIfNeeded({
            datasetId: selectedGeoData?.datasetId,
            ...formRef.current?.values,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error while saving the dataset', error);
        setErrorMessage('Could not save the dataset. Please try again or contact the support');
      } finally {
        setLoading(false);
      }
    }
    setSelectedGeoDataId(geoDataId);
  };

  const handleNextStepClick = async () => {
    if (selectedGeoDataId) {
      if (formRef.current?.values || selectedGeoData?.dataType !== EGeoDataEntity.DATASET) {
        const response = formRef.current?.values
          ? await handleSubmitVerification(formRef.current?.values)
          : undefined;

        const updatedDataset = response?.data?.updateDataset.dataset;
        const updatedGeoDataWithDataset = geoDataProcess?.geoDatas.find(
          geoData => geoData.datasetId === updatedDataset?.id
        );
        const remainingGeoDatas = geoDataProcess?.geoDatas.filter(
          geoData => geoData.id !== updatedGeoDataWithDataset?.id
        );

        if (
          (!updatedDataset ||
            updatedGeoDataWithDataset?.status === EGeoDataStatus.COMPLETED ||
            updatedGeoDataWithDataset?.status === EGeoDataStatus.NORMALISED) &&
          remainingGeoDatas?.every(
            geoData =>
              geoData.status === EGeoDataStatus.COMPLETED ||
              geoData.status === EGeoDataStatus.NORMALISED
          )
        ) {
          onNextClick();
        }
      }
    } else {
      onNextClick();
    }
  };

  return (
    <>
      <DueDiligenceProcessSubSectionNavigation
        allowNextStepNavigation
        nextStepLoading={loading}
        onNextStepClick={handleNextStepClick}
        onPreviousStepClick={onPreviousClick}
      >
        <Box display="flex" gap={2} flexWrap="wrap">
          {geoDataProcess.geoDatas.map(geoData => (
            <DatasetContainer key={geoData.id} onClick={() => handleGeoDataClick(geoData.id)}>
              <GeoDataItem
                title={geoData.title}
                commodity={geoData.rawMaterial?.title}
                countryCode={geoData.originCountry}
                owner={geoData?.ownedBy?.name}
                active={geoData.id === selectedGeoData?.id}
                status={
                  geoData.status === EGeoDataStatus.NORMALISED ||
                  geoData.status === EGeoDataStatus.COMPLETED
                    ? 'valid'
                    : 'error'
                }
                totalCount={geoData.totalOriginalSiteCount}
                includedCount={geoData.sites?.count}
                geoDataEntity={EGeoDataEntity[geoData.dataType]}
              />
            </DatasetContainer>
          ))}
        </Box>
      </DueDiligenceProcessSubSectionNavigation>

      <Box display="flex" flexDirection="column" mt={2} gap={2}>
        {!!selectedGeoData?.dataset && (
          <Formik<IDatasetFormValue>
            innerRef={formRef}
            enableReinitialize
            validationSchema={datasetSchema}
            initialValues={initialValues}
            onSubmit={handleSubmitVerification}
          >
            {formProps => (
              <DatasetForm
                {...formProps}
                showOnlyEudrRelevantRawMaterials
                submitButtonText="Save and verify"
              />
            )}
          </Formik>
        )}

        <Box position="relative">
          {selectedGeoData && (
            <GeoDataVerification
              geoData={selectedGeoData}
              isEUDR
              issues={selectedGeoData.issues}
              pageSubTitle="Farms in geo-data"
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default GeoDataCollectionVerification;
